/**
 * Route → default-doc mapping for the Docs_Slider.
 *
 * Encodes the ordered table from Requirement 6.1 of the help-docs-panel spec
 * and implements algorithm A3 (`routeToDefaultDoc`). First-match-wins on
 * `startsWith(route, prefix)`; falls back to `README.md` when no row matches.
 *
 * This module is a leaf utility — it deliberately does NOT import from
 * `./types.ts`. The row shape is inlined here so the compile-time constant
 * `ROUTE_MAPPING_TARGETS` can be consumed by the manifest generator without
 * pulling the entire view-type surface.
 *
 * @see .kiro/specs/help-docs-panel/requirements.md#requirement-6-route-to-default-section-mapping
 * @see .kiro/specs/help-docs-panel/design.md — Algorithm A3
 */

/**
 * Shape of a single row in {@link ROUTE_MAPPING_TABLE}.
 *
 * @remarks
 * - `prefix` — matched via `route.startsWith(prefix)`; MUST begin with `/`.
 * - `path` — a manifest path (relative to `/docs`, ending in `.md`).
 * - `anchor` — optional heading slug matching `/^[a-z0-9-]+$/`; scrolled into
 *   view within the render pane when present (Req 6.3).
 */
export interface RouteMappingRow {
  readonly prefix: string;
  readonly path: string;
  readonly anchor?: string;
}

/**
 * The ordered route → default-doc table (Requirement 6.1).
 *
 * Order is authoritative: the first row whose `prefix` satisfies
 * `route.startsWith(prefix)` wins. Do not reorder without amending Req 6.1.
 *
 * Row indices map 1:1 to the acceptance-criteria sub-numbers 6.1.1 … 6.1.11.
 *
 * @see .kiro/specs/help-docs-panel/requirements.md#requirement-6-route-to-default-section-mapping
 */
export const ROUTE_MAPPING_TABLE: readonly RouteMappingRow[] = Object.freeze([
  // 6.1.1
  { prefix: "/dashboard", path: "user-guide/index.md" },
  // 6.1.2
  { prefix: "/buildings", path: "features/import.md" },
  // 6.1.3
  { prefix: "/units", path: "architecture/models.md", anchor: "unit" },
  // 6.1.4
  { prefix: "/contacts", path: "contacts.md" },
  // 6.1.5
  {
    prefix: "/financial-records",
    path: "overview.md",
    anchor: "financial-records",
  },
  // 6.1.6
  { prefix: "/statements", path: "api/statements.md" },
  // 6.1.7
  { prefix: "/banking", path: "api/bank-account-edit.md" },
  // 6.1.8
  { prefix: "/reports", path: "overview.md", anchor: "reports" },
  // 6.1.9
  {
    prefix: "/building-compliance",
    path: "overview.md",
    anchor: "building-compliance",
  },
  // 6.1.10
  { prefix: "/import", path: "features/import.md" },
  // 6.1.11
  { prefix: "/errors/", path: "error-handling/guide.md" },
]);

/**
 * The deduplicated set of default-doc paths referenced by {@link ROUTE_MAPPING_TABLE}.
 *
 * Consumed by the build-time manifest generator (Requirement 5.4): the build
 * FAILS if any target here does not resolve to a manifest entry. Anchors are
 * intentionally excluded — only the underlying `.md` paths are verified.
 *
 * Ordered by first appearance in {@link ROUTE_MAPPING_TABLE} for stable output.
 *
 * @see .kiro/specs/help-docs-panel/requirements.md#requirement-5-docs-manifest-build-time-generator (5.4)
 * @see .kiro/specs/help-docs-panel/requirements.md#requirement-6-route-to-default-section-mapping (6.4)
 */
export const ROUTE_MAPPING_TARGETS: readonly string[] = Object.freeze(
  Array.from(new Set(ROUTE_MAPPING_TABLE.map((row) => row.path))),
);

/**
 * Resolve the default doc for a given route (algorithm A3).
 *
 * Iterates {@link ROUTE_MAPPING_TABLE} in order; the first row whose `prefix`
 * satisfies `route.startsWith(prefix)` wins. Returns `{ path: "README.md" }`
 * when nothing matches.
 *
 * @param route — the current pathname, e.g. `"/statements/abc-123"`. MUST be
 *   a non-empty string starting with `/`.
 * @returns `{ path, anchor? }` — `anchor` is omitted when the matching row has
 *   no anchor.
 * @throws {TypeError} when `route` is empty or does not start with `/`.
 *
 * @see .kiro/specs/help-docs-panel/design.md — Algorithm A3
 * @see .kiro/specs/help-docs-panel/requirements.md#requirement-6-route-to-default-section-mapping (6.1, 6.2)
 */
export function routeToDefaultDoc(
  route: string,
): { path: string; anchor?: string } {
  if (typeof route !== "string" || route.length === 0) {
    throw new TypeError(
      "routeToDefaultDoc: route must be a non-empty string starting with '/'",
    );
  }
  if (route[0] !== "/") {
    throw new TypeError(
      `routeToDefaultDoc: route must start with '/' (received: ${JSON.stringify(route)})`,
    );
  }

  for (const rule of ROUTE_MAPPING_TABLE) {
    if (route.startsWith(rule.prefix)) {
      return rule.anchor === undefined
        ? { path: rule.path }
        : { path: rule.path, anchor: rule.anchor };
    }
  }

  return { path: "README.md" };
}
